import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { getProductImage, serviceProducts } from '../../data/serviceProducts';
import { getServiceTypeImage } from '../../data/navServices';
import { formatEur, tndToEur } from '../../utils/currency';
import assetUrl from '../../assetUrl';
import './FeaturedDocs.css';

const CAROUSEL_ITEMS = [
  { kind: 'product', id: 'birth' },
  { kind: 'product', id: 'diploma' },
  { kind: 'product', id: 'passport' },
  { kind: 'product', id: 'criminal' },
  { kind: 'type', id: 'simultaneous' },
  { kind: 'type', id: 'consecutive' },
  { kind: 'type', id: 'businessInterpreting' },
  { kind: 'type', id: 'remoteInterpreting' },
];

export default function FeaturedDocs() {
  const { t, lang } = useLanguage();
  const f = t.homeFeatured;
  const locale = lang === 'ar' ? 'ar-TN' : lang === 'en' ? 'en-GB' : 'fr-FR';
  const trackRef = useRef(null);
  const timerRef = useRef(null);
  const [paused, setPaused] = useState(false);

  const onlineInterpreter =
    serviceProducts.find((p) => p.id === 'onlineInterpreter') || { price: 120, unit: 'hour' };

  const cards = CAROUSEL_ITEMS.map((item) => {
    if (item.kind === 'product') {
      const product = serviceProducts.find((p) => p.id === item.id);
      const copy = t.serviceCatalog.products[item.id];
      const rating = product.rating || 0;
      return {
        id: item.id,
        image: getProductImage(product),
        title: copy?.title || product.slug,
        price: product.price,
        unit: product.unit,
        rating,
        reviewLabel: rating.toLocaleString(locale),
      };
    }
    const type = t.serviceTypes.types[item.id];
    const rating = onlineInterpreter.rating || 0;
    return {
      id: item.id,
      image: getServiceTypeImage(item.id),
      title: type?.title || item.id,
      price: onlineInterpreter.price,
      unit: onlineInterpreter.unit,
      rating,
      reviewLabel: rating.toLocaleString(locale),
    };
  });

  const scroll = useCallback((dir) => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    if (dir === 1 && track.scrollLeft >= max - 10) {
      track.scrollTo({ left: 0, behavior: 'smooth' });
    } else if (dir === -1 && track.scrollLeft <= 10) {
      track.scrollTo({ left: max, behavior: 'smooth' });
    } else {
      track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    timerRef.current = setInterval(() => scroll(1), 4000);
    return () => clearInterval(timerRef.current);
  }, [paused, scroll]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') scroll(-1);
      if (e.key === 'ArrowRight') scroll(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [scroll]);

  return (
    <section className="section featured-docs">
      <div className="container">
        <div className="section-header">
          <h2>{f.title}</h2>
          <p>{f.subtitle}</p>
        </div>

        <div
          className="featured-docs-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <button
            type="button"
            className="featured-docs-arrow prev"
            onClick={() => scroll(-1)}
            aria-label="Précédent"
          >
            <ChevronLeft size={22} />
          </button>

          <div
            className="featured-docs-track"
            ref={trackRef}
          >
            {cards.map((card) => (
              <div key={card.id} className="featured-doc-card">
                <div className="featured-doc-media">
                  <img
                    src={assetUrl(card.image)}
                    alt={card.title}
                    loading="lazy"
                    decoding="async"
                  />
                  <Link to="/order" className="asc-order" aria-label="Commander en ligne">
                    <span>Commander</span>
                  </Link>
                </div>

                <div className="featured-doc-body">
                  <h3>{card.title}</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', gap: '2px', color: '#f5b041' }}>
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={14} fill={card.rating > 0 ? 'currentColor' : 'none'} aria-hidden="true" />
                      ))}
                    </div>
                    <span style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                      {card.reviewLabel} {t.serviceCatalog.reviews}
                    </span>
                  </div>
                  <p className="featured-doc-price">
                    <strong>{formatEur(tndToEur(card.price), locale)}</strong>
                    <span>
                      {card.price} {card.unit === 'hour'
                        ? t.serviceCatalog.perHour
                        : t.serviceCatalog.perPage}
                    </span>
                  </p>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="featured-docs-arrow next"
            onClick={() => scroll(1)}
            aria-label="Suivant"
          >
            <ChevronRight size={22} />
          </button>
        </div>

        <div className="featured-docs-more">
          <Link to="/tous-les-services" className="btn btn-outline">
            {f.seeMore}
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
